/**
 * 句子训练生成器（8 级循序渐进）
 * ------------------------------------------------------------
 *   L1 理解句意   听到/看到句子 → 选中文意思         （理解）
 *   L2 阅读判断   读句子判断正误                     （阅读）
 *   L3 挖空 1 处  挖掉 1 个关键词                    （挖空）
 *   L4 挖空多处   挖掉 2~3 个关键词                   （多挖空）
 *   L5 乱序重排   把打乱的词排成正确句子              （组织）
 *   L6 中译英     看中文写出英文整句                  （输出）
 *   L7 场景输出   给定情境用句型写句子                （应用）
 *   L8 独立默写   完全默写整句                        （记忆）
 *
 * 级别用 QUESTION_TYPES 里已有的 choice/judge/fill/order/transform 表达，
 * 所以判分器、错题分类（S 类）、掌握矩阵（8 级格子）都不需要额外代码。
 */
import { registerGenerator } from './registry.js';
import { makeQuestion } from '../../core/models.js';
import { shuffle, sample, pick, tokenize } from '../../utils/index.js';

export const SENTENCE_LEVELS = [
  { level: 1, name: '理解句意', skill: 'S', icon: '💡', desc: '看英文句子，选出正确的中文意思' },
  { level: 2, name: '阅读判断', skill: 'S', icon: '📖', desc: '读句子，判断中文翻译是否正确' },
  { level: 3, name: '挖空练习', skill: 'S', icon: '🕳️', desc: '句子中挖掉 1 个关键词，补全句子' },
  { level: 4, name: '多处挖空', skill: 'S', icon: '🕳️🕳️', desc: '句子中挖掉 2~3 处，全部补全' },
  { level: 5, name: '乱序重排', skill: 'S', icon: '🧩', desc: '把打乱的单词排成正确的句子' },
  { level: 6, name: '中译英', skill: 'S', icon: '✏️', desc: '看中文，写出完整的英文句子' },
  { level: 7, name: '场景输出', skill: 'S', icon: '🎭', desc: '根据情境，用本课句型写出句子' },
  { level: 8, name: '独立默写', skill: 'S', icon: '🧠', desc: '看中文，完整默写整句（无任何提示）' },
];

const STOP_FILL = new Set(['a', 'an', 'the', 'is', 'am', 'are', 'to', 'of', 'and', 'in', 'on', 'at', 'it', 'my']);

/** 选出适合挖空的词（优先实词、动词、介词短语） */
function pickBlanks(sentence, n) {
  const toks = tokenize(sentence);
  const candidates = toks
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => /[a-zA-Z]/.test(t) && !STOP_FILL.has(t.toLowerCase()));
  const chosen = sample(candidates, Math.min(n, Math.max(1, candidates.length - 1)));
  return chosen.map(c => c.i).sort((a, b) => a - b);
}

/** 用 ___ 替换指定位置的词，返回 { text, answers[] } */
function blankOut(sentence, idxs) {
  const toks = tokenize(sentence);
  const answers = idxs.map(i => toks[i]);
  idxs.forEach(i => { toks[i] = '____'; });
  return { text: toks.join(' ').replace(/\s+([.,!?;:])/g, '$1'), answers };
}

const base = (s, extra) => ({
  textbookId: s.textbookId,
  unitId: s.unitId,
  refType: 'sentence',
  refId: s.id,
  origin: 'generated',
  status: 'confirmed',
  ...extra,
});

function buildLevel(s, pool, level) {
  switch (level) {
    /* L1 理解句意 */
    case 1: {
      const others = sample(pool.filter(x => x.id !== s.id && x.zh), 3).map(x => x.zh);
      const opts = others.length >= 3 ? others : [...others, '这个句子的意思不太一样', '以上都不对'].slice(0, 3);
      return makeQuestion(base(s, {
        type: 'choice',
        level: 1,
        skill: 'S',
        prompt: `“${s.en}” 的意思是？`,
        audio: s.en,
        options: shuffle([s.zh, ...opts]),
        answer: [s.zh],
        explain: `${s.en} = ${s.zh}`,
        hint: s.pattern || '',
      }));
    }

    /* L2 阅读判断 */
    case 2: {
      // 一半概率给对的翻译，一半概率给错的翻译
      const useCorrect = Math.random() < 0.5;
      const wrongPool = pool.filter(x => x.id !== s.id && x.zh);
      const wrongZh = wrongPool.length ? pick(wrongPool).zh : '这句话的意思是别的';
      const shown = useCorrect ? s.zh : wrongZh;
      return makeQuestion(base(s, {
        type: 'judge',
        level: 2,
        skill: 'S',
        prompt: `读句子，判断下面的中文翻译是否正确：\n${s.en}\n→ ${shown}`,
        audio: s.en,
        options: ['正确', '错误'],
        answer: [useCorrect ? '正确' : '错误'],
        explain: useCorrect ? `正确。${s.en} = ${s.zh}` : `错误。正确意思是：${s.zh}`,
      }));
    }

    /* L3 挖空 1 处 */
    case 3: {
      const { text, answers } = blankOut(s.en, pickBlanks(s.en, 1));
      return makeQuestion(base(s, {
        type: 'fill',
        level: 3,
        skill: 'S',
        prompt: `补全句子：${text}`,
        answer: answers,
        accept: [],
        explain: `${s.en}\n${s.zh}`,
        hint: `首字母：${answers[0]?.[0] ?? ''}`,
      }));
    }

    /* L4 多处挖空 */
    case 4: {
      const n = 2 + (Math.random() < 0.4 ? 1 : 0);
      const { text, answers } = blankOut(s.en, pickBlanks(s.en, n));
      return makeQuestion(base(s, {
        type: 'fill',
        level: 4,
        skill: 'S',
        prompt: `补全句子（${answers.length} 处）：${text}`,
        answer: answers,
        explain: `${s.en}\n${s.zh}`,
        hint: `共 ${answers.length} 处，按顺序填写`,
      }));
    }

    /* L5 乱序重排 */
    case 5: {
      const toks = tokenize(s.en);
      let mixed = shuffle(toks);
      if (mixed.join(' ') === toks.join(' ')) mixed = shuffle(toks);
      return makeQuestion(base(s, {
        type: 'order',
        level: 5,
        skill: 'S',
        prompt: '把下面的单词排成一句通顺的英文',
        audio: s.en,
        tokens: mixed,
        options: mixed,
        answer: [s.en],
        explain: `${s.en}\n${s.zh}`,
        hint: `第一个词：${toks[0]}`,
      }));
    }

    /* L6 中译英 */
    case 6:
      return makeQuestion(base(s, {
        type: 'transform',
        level: 6,
        skill: 'S',
        prompt: `把中文翻译成英文：${s.zh}`,
        answer: [s.en],
        explain: `${s.en}\n${s.zh}`,
        hint: s.pattern || `首词：${tokenize(s.en)[0]}`,
      }));

    /* L7 场景输出 */
    case 7:
      return makeQuestion(base(s, {
        type: 'transform',
        level: 7,
        skill: 'S',
        prompt: `情境：${s.scene || `你需要表达「${s.zh}」`}\n请用本课学过的句型写一句英文。`,
        answer: [s.en],
        accept: s.accept || [],
        explain: `参考答案：${s.en}\n${s.zh}`,
        hint: s.pattern ? `可用句型：${s.pattern}` : '',
      }));

    /* L8 独立默写 */
    case 8:
      return makeQuestion(base(s, {
        type: 'transform',
        level: 8,
        skill: 'S',
        prompt: `默写整句：${s.zh}`,
        answer: [s.en],
        explain: `${s.en}`,
        hint: '',
      }));

    default:
      return null;
  }
}

function build({ content, level = 1, count = 10, priority = null } = {}) {
  const pool = content.sentences || [];
  if (!pool.length) return [];

  // 重点句型优先
  const sorted = priority?.size
    ? [...pool.filter(s => priority.has(s.id)), ...shuffle(pool.filter(s => !priority.has(s.id)))]
    : [...shuffle(pool.filter(s => s.keyPattern)), ...shuffle(pool.filter(s => !s.keyPattern))];

  const list = [];
  let i = 0;
  while (list.length < count && i < count * 3) {
    list.push(sorted[i % sorted.length]);
    i++;
  }
  return list.map(s => buildLevel(s, pool, level)).filter(Boolean).slice(0, count);
}

export const sentenceGenerator = registerGenerator({
  id: 'sentence-levels',
  module: 'sentence',
  title: '句子训练',
  levels: SENTENCE_LEVELS,
  build,
});

export default sentenceGenerator;
